import React, { useEffect, useState } from "react";

import { Card } from "primereact/card";
import { Chips } from "primereact/chips";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";

import { TooltipOptions } from "primereact/tooltip/tooltipoptions";

import { Button } from "primereact/button";

import { classNames } from "primereact/utils";

import { Customer } from "../../../../types/customer";
import { validateIBAN } from "../../utils/iban-validation";

import StationIdDropdown from "../../AddCustomer/components/StationIdDropdown";
import IndustryDropdown from "../../../../components/inputs/IndustryDropdown";
import CountryDropdown from "../../../../components/inputs/CountryDropdown";
import { ISO_COUNTRY_OPTIONS } from "../../../../components/inputs/consts";
import LanguageDropdown, {
  LANGUGAGE_OPTIONS,
} from "../../../../components/inputs/LanguageDropdown";
import { RadioButton } from "primereact/radiobutton";

interface CustomerFormInputProps {
  id: string;
  label: string;
  value: string | undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  type?: "text" | "email" | "tel" | "password";
  disabled?: boolean;
  nested?: boolean;
  tooltip?: string;
  tooltipOptions?: TooltipOptions;
  errorMessage?: string | null;
}

const CustomerFormTextInput = ({
  id,
  label,
  value = "",
  onChange,
  placeholder = "",
  className = "",
  required = false,
  type = "text",
  disabled = false,
  tooltip = undefined,
  tooltipOptions = undefined,
  errorMessage = null,
}: CustomerFormInputProps) => {
  return (
    <div className={classNames("field", className)}>
      <label htmlFor={id} className="font-medium">
        {label}
        {required && <span> *</span>}
      </label>
      <InputText
        id={id}
        type={type}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className={classNames("w-full", { "p-invalid": errorMessage })}
        tooltip={tooltip}
        tooltipOptions={tooltipOptions}
      />
      {errorMessage && (
        <small className="p-error">{errorMessage}</small>
      )}
    </div>
  );
};

interface CustomerFormProps {
  customer: Customer;
  onFieldChange: (field: string, value: any, nested?: boolean) => void;
  onSubmit: () => Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
  title?: string;
  isLoading?: boolean;
  disabled?: boolean;
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  customer,
  onFieldChange,
  onSubmit,
  submitLabel = "Submit",
  onCancel = undefined,
  title = "Customer Details",
  isLoading = false,
  disabled = false,
}) => {
  const [ibanError, setIbanError] = useState<string | null>(null);

  // UI toggle only, allows you to enter vat_id when true
  const [showVatOptions, setShowVatOptions] = useState(!!customer?.vat_id);

  useEffect(() => {
    // clears the vat_id when include VAT is unchecked
    if (!showVatOptions) {
      onFieldChange("vat_id", "");
    }
  }, [showVatOptions, onFieldChange]);

  return (
    <Card title={title} className="p-4">
      <div className="pt-2 p-fluid">
        {/* Bank Details */}
        <section className="mb-5">
          <h3 className="text-xl font-semibold text-primary mb-3">
            Bank Details
          </h3>
          <section className="grid">
            <CustomerFormTextInput
              id="account_holder_name"
              label="Account Holder"
              value={customer.account_holder_name}
              onChange={(e) =>
                onFieldChange("account_holder_name", e.target.value)
              }
              required
              className="col-12 md:col-4"
              disabled={disabled}
            />
            <CustomerFormTextInput
              id="iban"
              label="IBAN"
              value={customer.iban}
              onChange={(e) => {
                const value = e.target.value;
                onFieldChange("iban", value);
                setIbanError(validateIBAN(value));
              }}
              required
              className="col-12 md:col-4"
              disabled={disabled}
              errorMessage={ibanError}
            />
            <CustomerFormTextInput
              id="bic"
              label="BIC"
              required
              value={customer.bic}
              onChange={(e) => onFieldChange("bic", e.target.value)}
              className="col-12 md:col-4"
              disabled={disabled}
            />
          </section>
        </section>

        {/* Contact Info */}
        <section className="mb-5">
          <h3 className="text-xl font-semibold text-primary mb-3">
            Contact Info
          </h3>

          <section className="grid">
            <CustomerFormTextInput
              id="email"
              label="Primary Email"
              type="email"
              value={customer.email}
              onChange={(e) => onFieldChange("email", e.target.value)}
              required
              className="col-12 md:col-6"
              disabled={disabled}
            />

            <CustomerFormTextInput
              id="phone"
              label="Phone"
              type="tel"
              required
              value={customer.phone}
              onChange={(e) => onFieldChange("phone", e.target.value)}
              className="col-12 md:col-6"
              disabled={disabled}
            />

            <div className="field col-12 md:col-12">
              <label className="font-medium">
                Additional Recipient Email(s)
              </label>
              <Chips
                value={customer.recipient_emails ?? []}
                onChange={(e) => onFieldChange("recipient_emails", e.value)}
                allowDuplicate={false}
                className="w-full"
                disabled={disabled}
              />
            </div>
          </section>
        </section>

        {/* Address*/}
        <section className="mb-5">
          <h3 className="text-xl font-semibold text-primary mb-3">Address</h3>

          <section className="grid">
            <CustomerFormTextInput
              id="company_name"
              label="Company Name"
              value={customer.address.company_name}
              onChange={(e) =>
                onFieldChange("address.company_name", e.target.value, true)
              }
              className="col-12 md:col-6"
              disabled={disabled}
            />

            <CustomerFormTextInput
              required
              id="recipient_name"
              label="Recipient Name"
              value={customer.address.recipient_name}
              onChange={(e) =>
                onFieldChange("address.recipient_name", e.target.value, true)
              }
              className="col-12 md:col-6"
              disabled={disabled}
            />

            <CustomerFormTextInput
              id="street_address"
              label="Street"
              value={customer.address.street_address}
              onChange={(e) =>
                onFieldChange("address.street_address", e.target.value, true)
              }
              required
              className="col-12 md:col-6"
              disabled={disabled}
            />
            <CustomerFormTextInput
              id="postal_code"
              label="Postal Code"
              value={customer.address.postal_code}
              onChange={(e) =>
                onFieldChange("address.postal_code", e.target.value, true)
              }
              required
              className="col-12 md:col-2"
              disabled={disabled}
            />
            <CustomerFormTextInput
              id="city"
              label="City"
              value={customer.address.city}
              onChange={(e) =>
                onFieldChange("address.city", e.target.value, true)
              }
              required
              className="col-12 md:col-4"
              disabled={disabled}
            />

            <div className="field col-12 md:col-4">
              <label className="font-medium">
                Country <span> *</span>
              </label>
              <CountryDropdown
                value={
                  ISO_COUNTRY_OPTIONS.find(
                    (o) => o.code === customer.address.country
                  ) || null
                }
                onChange={(opt) =>
                  onFieldChange("address.country", opt?.code ?? "DE", true)
                }
              />
            </div>
          </section>
        </section>

        {/* Invoice Settings */}
        <section className="mb-5">
          <h3 className="text-xl font-semibold text-primary mb-3">
            Invoice Preferences
          </h3>

          <section className="grid">
            <div className="field col-12 md:col-9">
              <label className="font-medium">Invoice Language</label>
              <LanguageDropdown
                value={
                  LANGUGAGE_OPTIONS.find(
                    (o) => o.code === customer.invoice_language
                  ) || null
                }
                onChange={(opt) =>
                  onFieldChange("invoice_language", opt?.code ?? "DE")
                }
              />
            </div>

            <div className="field col-12 md:col-3 flex align-items-center gap-3 my-5">
              <Checkbox
                id="detailed_invoice"
                checked={customer.detailed_invoice}
                onChange={(e) => onFieldChange("detailed_invoice", e.checked)}
                disabled={disabled}
              />
              <label htmlFor="detailed_invoice" className="my-2">
                Detailed Invoice
              </label>
            </div>

            <div className="field col-12 md:col-3 flex align-items-center gap-3 my-5">
              <Checkbox
                id="attach_csv_to_credit_note"
                checked={customer.attach_csv_to_credit_note}
                onChange={(e) =>
                  onFieldChange("attach_csv_to_credit_note", e.checked)
                }
                disabled={disabled}
              />
              <label htmlFor="attach_csv_to_credit_note" className="my-2">
                Attach CSV to credit note
              </label>
            </div>
          </section>
        </section>

        {/* VAT Options */}
        <section className="mb-5">
          <h3 className="text-xl font-semibold text-primary mb-3">
            VAT Options
          </h3>

          <section className="grid">
            {/* Customer Business Type */}
            <div className="field col-12 md:col-9">
              <label className="font-medium">
                Customer Type <span>*</span>
              </label>
              <div className="flex flex-wrap gap-4 mt-2">
                <div className="flex align-items-center">
                  <RadioButton
                    inputId="business_type_private"
                    name="business_type"
                    value="PRIVATE"
                    onChange={(e) => onFieldChange("business_type", e.value)}
                    checked={customer.business_type === "PRIVATE"}
                    disabled={disabled}
                  />
                  <label htmlFor="business_type_private" className="ml-2">
                    Private
                  </label>
                </div>
                <div className="flex align-items-center">
                  <RadioButton
                    inputId="business_type_business"
                    name="business_type"
                    value="BUSINESS"
                    onChange={(e) => onFieldChange("business_type", e.value)}
                    checked={customer.business_type === "BUSINESS"}
                    disabled={disabled}
                  />
                  <label htmlFor="business_type_business" className="ml-2">
                    Business
                  </label>
                </div>
              </div>
            </div>

            <div className="field col-12  md:col-3 flex align-items-center gap-3">
              <Checkbox
                id="include_vat"
                checked={showVatOptions}
                onChange={(e) => setShowVatOptions(e.checked)}
              />
              <label htmlFor="include_vat" className="my-2">
                Include VAT
              </label>
            </div>

            {/* VAT ID */}
            {showVatOptions && (
              <CustomerFormTextInput
                id="vat_id"
                label="VAT ID"
                value={customer.vat_id}
                onChange={(e) => onFieldChange("vat_id", e.target.value)}
                required
                className="col-12 md:col-6"
                disabled={disabled}
                tooltip="Required when VAT is included"
              />
            )}
          </section>
        </section>

        <section className="mb-5">
          <h3 className="text-xl font-semibold text-primary mb-3">
            Additional Info
          </h3>

          <section className="grid">
            <div className="field col-12">
              <label className="font-medium">Industry</label>
              <IndustryDropdown
                valueCode={customer.industry}
                onChangeCode={(code) => onFieldChange("industry", code)}
                placeholder="Select an industry"
                className="w-full"
              />
            </div>
          </section>
        </section>

        <section className="mb-5">
          <h3 className="text-xl font-semibold text-primary mb-3">
            Associated station (optional)
          </h3>

          <section className="grid">
            <div className="field col-12">
              <label className="font-medium">Station ID</label>
              <StationIdDropdown
                valueCode={customer.station_id}
                onChangeCode={(code) => onFieldChange("station_id", code)}
                placeholder="Assign to station"
                className="w-full"
              />
            </div>
          </section>
        </section>

        {/* Action Buttons */}
        <div className="field flex flex-column col-12 md:flex-row md:justify-content-end gap-2">
          <Button
            className="p-button w-full md:w-20rem"
            label={submitLabel}
            onClick={onSubmit}
            loading={isLoading}
            disabled={isLoading || disabled}
          />

          {onCancel && (
            <Button
              className="p-button w-full md:w-20rem"
              label="Cancel"
              outlined
              onClick={onCancel}
            />
          )}
        </div>
      </div>
    </Card>
  );
};

export default CustomerForm;
